import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useMemo } from "react";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { useThirdPartyIntegrations } from "../../../hooks";
import { SalesChannelCard } from "../../ui/SalesChannels";
import { IntegrationFilters, FeatureGate } from "../../ui";
import { SUBSCRIPTION_FEATURES } from "../../../lib/subscriptionFeatures";
import "./integrations-ordering.css";

const SalesChannelsPage = ({ selectedBrand }) => {
  const { t } = useTranslation("integrations");
  const { data, isLoading, error } = useThirdPartyIntegrations();
  const [searchTerm, setSearchTerm] = useState("");

  // Filter only Sales Channel integrations
  const salesChannelIntegrations = useMemo(() => {
    if (!data?.integrations) return [];
    return data.integrations.filter(
      (integration) => integration.type === "sales_channel",
    );
  }, [data?.integrations]);

  // Filter sales channel integrations locally based on search term
  const filteredSalesChannelIntegrations = useMemo(() => {
    if (!searchTerm.trim()) {
      return salesChannelIntegrations;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return salesChannelIntegrations.filter((integration) =>
      integration.name?.toLowerCase().includes(lowerSearchTerm),
    );
  }, [salesChannelIntegrations, searchTerm]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  return (
    <FeatureGate
      featureKey={SUBSCRIPTION_FEATURES.INTEGRATIONS_SALES_CHANNELS}
      featureName={t("salesChannels.header.title")}
    >
      <div className="ordering-integrations-page">
        <PageHeader
          title={t("salesChannels.header.title")}
          subtitle={t("salesChannels.header.subtitle")}
          selectedBrand={selectedBrand}
        />

        {/* Filters */}
        {!isLoading && !error && (
          <div className="ordering-integrations-filters">
            <IntegrationFilters
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("salesChannels.searchPlaceholder")}
            />
          </div>
        )}

        <div className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="ordering-integration-state border-border bg-background animate-pulse rounded-lg border p-6"
                >
                  <div className="bg-card mb-3 h-12 w-12 rounded-lg"></div>
                  <div className="bg-card mb-2 h-6 w-3/4 rounded"></div>
                  <div className="bg-card mb-4 h-4 w-1/2 rounded"></div>
                  <div className="mb-4 space-y-2">
                    <div className="bg-card h-3 rounded"></div>
                    <div className="bg-card h-3 rounded"></div>
                    <div className="bg-card h-3 rounded"></div>
                  </div>
                  <div className="bg-card h-10 rounded"></div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="ordering-integration-state border-destructive/30 bg-destructive/20 rounded-lg border p-6">
              <div className="flex items-center space-x-3">
                <div className="text-destructive">
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-destructive text-lg font-medium">
                    {t("salesChannels.error.title")}
                  </h3>
                  <p className="text-destructive mt-1 text-sm">
                    {error.message || t("salesChannels.error.defaultMessage")}
                  </p>
                  <Button
                    emphasis="solid"
                    intent="danger"
                    size="touch"
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 transition-colors"
                  >
                    {t("actions.retry")}
                  </Button>
                </div>
              </div>
            </div>
          ) : filteredSalesChannelIntegrations.length === 0 ? (
            <div className="ordering-integration-state py-12 text-center">
              <div className="bg-card mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full">
                <svg
                  className="text-muted-foreground h-12 w-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
              </div>
              <h3 className="text-foreground mb-2 text-lg font-medium">
                {searchTerm
                  ? t("salesChannels.empty.searchTitle")
                  : t("salesChannels.empty.defaultTitle")}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm
                  ? t("salesChannels.empty.searchDescription", { searchTerm })
                  : t("salesChannels.empty.defaultDescription")}
              </p>
            </div>
          ) : (
            <div className="ordering-integration-grid grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredSalesChannelIntegrations.map((integration) => (
                <SalesChannelCard
                  key={integration.id}
                  integration={integration}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
};

export default SalesChannelsPage;
